import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ShoppingBag, Shirt, Cpu, Gem, ToyBrick, Home as HomeIcon, Sparkles, Truck } from "lucide-react";
import { BRAND } from "@/data/brand";
import { GorillaGuarantee } from "@/components/GorillaGuarantee";
import { api } from "@/lib/api";
import beachBanner from "@/assets/brand/beach-banner.jpg";
import { Seo } from "@/lib/seo";

const OFFERINGS = [
  { icon: ShoppingBag, label: "Personally selected resale finds" },
  { icon: Shirt, label: "Clothing" },
  { icon: ShoppingBag, label: "Handbags" },
  { icon: Cpu, label: "Electronics" },
  { icon: Gem, label: "Collectibles" },
  { icon: ToyBrick, label: "Toys" },
  { icon: HomeIcon, label: "Home goods" },
  { icon: Sparkles, label: "Interesting & unusual finds" },
  { icon: Truck, label: "A separate supplier-shipped selection" },
];

export default function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const c = content || {};
  const paragraphs = (c.about_story || "").split("\n\n").filter(Boolean);

  return (
    <div data-testid="about-page">
      <Seo title="About JohnnyMart" path="/about" description="The second-chance story behind JohnnyMart — a Ponte Vedra Beach, FL resale shop offering personally selected finds and a supplier-shipped selection, at honest prices." />
      <section
        className="relative h-72 sm:h-80 flex items-end"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(6,33,61,.85), rgba(6,33,61,.35)), url(${beachBanner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="jm-container pb-10">
          <p className="uppercase tracking-[0.2em] text-gold font-bold text-sm">About JohnnyMart</p>
          <h1 className="font-display text-5xl sm:text-6xl text-cream mt-2">{BRAND.tagline}</h1>
        </div>
      </section>

      {/* Meet the Owner */}
      <section data-testid="meet-owner-section" className="jm-container py-16">
        <div className="grid gap-10 lg:grid-cols-3 items-start">
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {c.owner_photo ? (
                <img
                  data-testid="owner-photo"
                  src={c.owner_photo}
                  alt={c.owner_photo_alt || "Johnny, owner of JohnnyMart"}
                  className="w-full max-w-xs mx-auto lg:mx-0 rounded-xl object-cover shadow-lg border-4 border-cream aspect-[4/5]"
                />
              ) : (
                <div data-testid="owner-photo-placeholder" className="w-full max-w-xs mx-auto lg:mx-0 rounded-xl bg-navy/5 border-2 border-dashed border-navy/20 aspect-[4/5] flex flex-col items-center justify-center text-center p-6">
                  <ShoppingBag className="w-10 h-10 text-navy/30" />
                  <p className="mt-3 text-sm text-navy/50 font-semibold">Owner photo coming soon</p>
                </div>
              )}
              <div className="mt-4 inline-flex items-center gap-2 text-navy font-semibold">
                <MapPin className="w-5 h-5 text-brick" /> {BRAND.location}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">{c.about_heading || "Meet Johnny"}</p>
            <h2 className="font-display text-4xl text-navy mt-2">A second-chance story behind every find</h2>
            <div data-testid="owner-story" className="mt-5 space-y-4">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-ink/80 leading-relaxed">{p}</p>
              ))}
            </div>
            {c.about_tagline && (
              <p className="mt-6 font-display text-2xl text-brick">{c.about_tagline}</p>
            )}
            <div className="mt-8">
              <Link to="/shop" className="bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors">
                Browse the Shop
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What JohnnyMart offers */}
      <section data-testid="offerings-section" className="bg-[#fff8ec]">
        <div className="jm-container py-16">
          <div className="text-center max-w-2xl mx-auto">
            <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">What we carry</p>
            <h2 className="font-display text-4xl text-navy mt-2">Honest finds, fair prices, friendly service</h2>
            <p className="text-ink/70 mt-3">
              Personally selected resale treasures plus a separate supplier-shipped selection — there's always
              something new, useful, collectible, or just plain cool to discover.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {OFFERINGS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-lg bg-white border border-navy/15 px-5 py-4">
                <Icon className="w-5 h-5 text-brick shrink-0" />
                <span className="font-semibold text-navy">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GorillaGuarantee />
    </div>
  );
}
